/**
 * Application-level cache for PDF file data.
 *
 * Pre-signed S3 URLs change their query parameters (signature, expiry) on every
 * fetch, which defeats the browser's HTTP cache. This module fetches the PDF once
 * and creates a stable blob URL keyed by the URL pathname so subsequent
 * opens of the same document reuse the cached blob URL without a network round-trip.
 */

const cache = new Map<string, string>();

/** Extracts the URL pathname to use as a stable cache key (ignores query params). */
function cacheKey(url: string): string {
	try {
		return new URL(url).pathname;
	} catch {
		return url;
	}
}

/** Returns cached blob URL for the given URL, or `undefined` if not cached. */
export function getCachedPdfUrl(url: string): string | undefined {
	return cache.get(cacheKey(url));
}

/** Fetches the PDF, stores a blob URL in the cache, and returns it. */
export async function fetchAndCachePdf(url: string): Promise<string> {
	const key = cacheKey(url);
	const existing = cache.get(key);
	if (existing) return existing;

	const response = await fetch(url);
	const data = await response.arrayBuffer();
	const blob = new Blob([data], { type: 'application/pdf' });
	const blobUrl = URL.createObjectURL(blob);
	cache.set(key, blobUrl);
	return blobUrl;
}

/** Removes a specific entry from the cache and revokes the blob URL. */
export function evictCachedPdf(url: string): void {
	const key = cacheKey(url);
	const blobUrl = cache.get(key);
	if (blobUrl) {
		URL.revokeObjectURL(blobUrl);
		cache.delete(key);
	}
}
